import { useEffect, useRef } from "react";
import { colors } from "./constants"
import { navigateBible, useUrlQueryParam } from "./routing"
import BibleLink from "./BibleLink"
import { faArrowRight, faArrowUpAZ, faArrowUpWideShort, faFilter, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton } from "./BibleTopBar";
import { useScreenSize } from "../hooks/useScreenSize";
import { mergeHighlight } from "./BibleContent";
import { HideChromeButton } from "./ChromeVisibility";

interface TagProps {
  name: string;
  label: string;
  active?: boolean;
  onClick?: (name: string) => void;
  endIcon: any;
}

function Tag({
  name,
  label,
  active = false,
  onClick = () => { },
  endIcon
}: TagProps) {
  return (
    <button
      style={{
        border: '1px solid',

        backgroundColor: active ? 'var(--active-button-background)' : colors['buttonBackground'],
        borderColor: active ? 'var(--active-button-border)' : colors['buttonBorder'],
        color: active ? 'var(--active-button-text)' : colors['buttonText'],

        padding: '4px 8px',
        fontSize: '12px',
        display: 'flex',
        gap: '8px',
        cursor: 'pointer',
      }}
      onClick={() => onClick(name)}
    >
      <div style={{
        flex: 1
      }}>
        {label}
      </div>
      <div style={{
        fontWeight: 'bold'
      }}>
        {/* {count} */}
        <FontAwesomeIcon icon={endIcon} />
      </div>
    </button>
  )
}

interface SideBarTagsProps {
  filters: {
    testament: 'all' | 'new' | 'old';
    sortOrder: 'traditional' | 'alphabetical';
  };
  setFilters: (filters: {
    testament: 'all' | 'new' | 'old';
    sortOrder: 'traditional' | 'alphabetical';
  }) => void;
}

function SideBarTags({
  filters,
  setFilters
}: SideBarTagsProps) {
  return (
    <div style={{
      width: '100%',
      borderBottom: `1px solid ${colors['borderColor']}`,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      padding: '4px'
    }}>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'row',
        gap: '4px',
      }}>
        <Tag
          label="All"
          name="all"
          endIcon={faFilter}
          active={filters.testament === 'all'}
          onClick={() => {
            const newFilters = { ...filters, testament: 'all' as const };
            setFilters(newFilters);
          }}
        />
        <Tag
          label="New Testament"
          name="new_testament"
          endIcon={faFilter}
          active={filters.testament === 'new'}
          onClick={() => {
            const newFilters = { ...filters, testament: 'new' as const };
            setFilters(newFilters);
          }}
        />
        <Tag
          label="Old Testament"
          name="old_testament"
          endIcon={faFilter}
          active={filters.testament === 'old'}
          onClick={() => {
            const newFilters = { ...filters, testament: 'old' as const };
            setFilters(newFilters);
          }}
        />
      </div>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'row',
        gap: '4px',
      }}>
        <Tag
          label="Traditional"
          name="traditional"
          endIcon={faArrowUpWideShort}
          active={filters.sortOrder === 'traditional'}
          onClick={() => {
            const newFilters = { ...filters, sortOrder: 'traditional' as const };
            setFilters(newFilters);
          }}
        />
        <Tag
          label="Alphabetical"
          name="alphabetical"
          endIcon={faArrowUpAZ}
          active={filters.sortOrder === 'alphabetical'}
          onClick={() => {
            const newFilters = { ...filters, sortOrder: 'alphabetical' as const };
            setFilters(newFilters);
          }}
        />
      </div>
    </div>
  )
}

interface BookItemProps {
  currentTranslation: any;
  currentBook: any;
  currentChapter: any;
  bookItem: any;
  chosenBook: any;
  itemRef?: React.Ref<HTMLDivElement>;
  clearQuery?: boolean;
}

export function BookItem({ currentTranslation, currentBook, currentChapter, bookItem, chosenBook, itemRef, clearQuery = false }: BookItemProps) {
  const isChosen = bookItem.slug === chosenBook['slug'];

  return (
    <div ref={itemRef}>
      <BibleLink
        currentTranslationSlug={currentTranslation.slug}
        currentBookSlug={currentBook.slug}
        currentChapterNumber={currentChapter.chapter_number}
        bookSlug={bookItem.slug}
        chapterNumber={1}
        clearQuery={clearQuery}
        style={{
          display: 'flex',
          textDecoration: 'none',
          color: 'inherit',
          border: `1px solid ${colors['borderColor']}`,
          padding: '8px',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          backgroundColor: isChosen ? colors['light'] : colors['white']
        }}
      >
        <div style={{
          color: isChosen ? colors['darkPrimary'] : colors['black'],
        }}>
          {bookItem.name}
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <input
            type="radio"
            name="book"
            value={bookItem.slug}
            style={{
              cursor: 'pointer',
              border: `1px solid ${colors['borderColor']}`,
              borderRadius: '0%',
            }}
            checked={isChosen}
            readOnly
          />
        </div>
      </BibleLink>
    </div>
  )
}

interface BooksListProps {
  currentTranslation: any;
  currentBook: any;
  currentChapter: any;
  books: any[];
  chosenBook: any;
  clearQuery?: boolean;
  scrollToChosenOnUpdate?: boolean;
}

export function BooksList({ currentTranslation, currentBook, currentChapter, books, chosenBook, clearQuery = false, scrollToChosenOnUpdate = false }: BooksListProps) {
  const chosenItemRef = useRef<HTMLDivElement>(null);
  const hasScrolledOnMount = useRef(false);

  useEffect(() => {
    const isInIframe = window.self !== window.top;
    if (!isInIframe && !hasScrolledOnMount.current && chosenItemRef.current) {
      chosenItemRef.current.scrollIntoView({ block: 'center', behavior: 'auto' });
      hasScrolledOnMount.current = true;
    }
  }, []);

  const isInitialBooksRender = useRef(true);
  useEffect(() => {
    if (isInitialBooksRender.current) {
      isInitialBooksRender.current = false;
      return;
    }
    if (scrollToChosenOnUpdate && chosenItemRef.current) {
      chosenItemRef.current.scrollIntoView({ block: 'center', behavior: 'auto' });
      hasScrolledOnMount.current = true;
    }
  }, [books, scrollToChosenOnUpdate]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    }}>
      {books.map((book: any) => {
        const isChosen = book.slug === chosenBook['slug'];
        return (
          <BookItem
            key={`bible-book-${book.id}`}
            currentTranslation={currentTranslation}
            currentBook={currentBook}
            currentChapter={currentChapter}
            bookItem={book}
            chosenBook={chosenBook}
            itemRef={isChosen ? chosenItemRef : undefined}
            clearQuery={clearQuery}
          />
        );
      })}
    </div>
  )
}

interface QueriedVerseItemProps {
  currentTranslation: any;
  currentBook: any;
  currentChapter: any;
  verse: any;
  onNavigate?: () => void;
}


export function QueriedVerseItem({ currentTranslation, currentBook, currentChapter, verse, onNavigate }: QueriedVerseItemProps) {
  const bibleReference = verse[1].bible_reference || verse[0].bible_reference;
  const text = mergeHighlight(verse[0].text, verse[1].text);

  // Check if this verse is from the currently opened chapter
  const isActive =
    verse[0].bible_translation_slug == currentTranslation.slug &&
    verse[0].bible_book_slug == currentBook.slug &&
    verse[0].chapter_number == currentChapter.chapter_number;

  const { screenLargerThan } = useScreenSize()
  const onMobile = !screenLargerThan('lg');
  return (
    <div style={{ border: `1px solid ${colors['borderColor']}` }}>
      <BibleLink
        currentTranslationSlug={currentTranslation.slug}
        currentBookSlug={currentBook.slug}
        currentChapterNumber={currentChapter.chapter_number}
        translationSlug={verse[0].bible_translation_slug}
        bookSlug={verse[0].bible_book_slug}
        chapterNumber={verse[0].chapter_number}
        verseNumber={verse[0].verse_number}
        clearQuery={onMobile}
        onClick={onMobile ? onNavigate : undefined}
        style={{
          display: 'flex',
          textDecoration: 'none',
          borderBottom: `1px solid ${colors['borderColor']}`,
          padding: '2px 4px',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          backgroundColor: isActive ? 'var(--active-button-background)' : colors['buttonBackground'],
          borderColor: isActive ? 'var(--active-button-border)' : colors['buttonBorder'],
          color: isActive ? 'var(--active-button-text)' : colors['buttonText'],
        }}>
        <span dangerouslySetInnerHTML={{ __html: bibleReference }} />
        <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '8px' }} />
      </BibleLink>
      <div style={{
        padding: '4px',
        backgroundColor: colors['white'],
        color: colors['black'],
        direction: verse[0].rtl ? 'rtl' : 'ltr',
        userSelect: 'text',
      }}>
        <span dangerouslySetInnerHTML={{ __html: text }} />
      </div>
    </div>
  )
}

interface QueriedVersesProps {
  currentTranslation: any;
  currentBook: any;
  currentChapter: any;
  verses: any;
  onNavigate?: () => void;
}

export function QueriedVerses({ currentTranslation, currentBook, currentChapter, verses, onNavigate }: QueriedVersesProps) {
  if (Object.keys(verses).length === 0) {
    return null;
  }
  return (
    <div
      style={{
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}>
      {verses.map((verse: any) => (
        <QueriedVerseItem
          key={`queried-verse-${verse[0].id}`}
          currentTranslation={currentTranslation}
          currentBook={currentBook}
          currentChapter={currentChapter}
          verse={verse}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  )
}

interface SideBarProps {
  chosenTranslation: any;
  chosenBook: any;
  chosenChapter: any;
  queriedBooks: any[];
  queriedVerses: any[];
  queryInput: string;
  setQueryInput: (value: string) => void;
  onHide: () => void;
}

export default function BibleSideBar({
  chosenTranslation,
  chosenBook,
  chosenChapter,
  queriedBooks,
  queriedVerses,
  queryInput,
  setQueryInput,
  onHide,
}: SideBarProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [testament, setTestament] = useUrlQueryParam('testament', 'all');
  const [sort, setSort] = useUrlQueryParam('sort', 'traditional');

  const currentLocation = {
    translationSlug: chosenTranslation.slug,
    bookSlug: chosenBook.slug,
    chapterNumber: chosenChapter.chapter_number,
  };

  // Reset scroll position when query changes (but not on initial mount,
  // so the chosen book can still scroll into view)
  const isInitialQueryRender = useRef(true);
  useEffect(() => {
    if (isInitialQueryRender.current) {
      isInitialQueryRender.current = false;
      return;
    }
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [queryInput]);

  return (
    <div style={{
      height: '100%',
      width: '350px',
      display: 'flex',
      flexDirection: 'column',
      borderRight: `1px solid ${colors['borderColor']}`,
      backgroundColor: colors['background']
    }}>
      {/* SideBar Headers */}
      <div style={{
        width: '100%',
        height: '50px',
        borderBottom: `1px solid ${colors['borderColor']}`,
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
      }}>
        <textarea
          placeholder="Search: Son of Man, John 3:16, Matthew..."
          className="custom-input focus:outline-none focus:ring-0 focus:border-transparent focus:shadow-none"
          value={queryInput}
          style={{
            display: 'block',
            flex: 1,
            minWidth: 0,
            height: '50px',
            border: 'none',
            padding: '8px',
            paddingRight: queryInput ? '40px' : '8px',
            fontSize: '16px',
            boxSizing: 'border-box',
            outline: 'none',
            backgroundColor: 'transparent',
            color: colors['black'],
            resize: 'none',
            overflowY: 'auto',
            lineHeight: '1.4',
            fontFamily: 'inherit',
          }}
          onChange={(e) => {
            navigateBible(currentLocation, { query: e.target.value })
            setQueryInput(e.target.value)
          }}
        />
        {queryInput && (
          <div style={{
            position: 'absolute',
            top: '4px',
            right: '32px',
          }}>
            <IconButton
              icon={faXmark}
              style={{ minWidth: '28px', height: '28px', fontSize: '12px' }}
              onClick={() => {
                navigateBible(currentLocation, { query: '' })
                setQueryInput('')
              }}
            />
          </div>
        )}
        <HideChromeButton
          onHide={onHide}
          label="Hide search sidebar"
          size={24}
          style={{ color: colors['borderColor'], marginTop: '4px', marginRight: '4px' }}
        />
      </div>
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <SideBarTags
          filters={{
            testament,
            sortOrder: sort
          } as any}
          setFilters={({ testament: newTestament, sortOrder: newSortOrder }) => {
            navigateBible(currentLocation, {
              testament: newTestament,
              sortOrder: newSortOrder
            })
            setTestament(newTestament)
            setSort(newSortOrder)
          }}
        />
        <div
          ref={scrollContainerRef}
          style={{
            padding: '4px',
            overflow: 'auto',
          }}>
          {queryInput && !queriedBooks?.length && !queriedVerses?.length ? (
            <div style={{
              padding: '16px',
              textAlign: 'center',
              color: colors['black'],
            }}>
              <div>
                No results in this translation ({chosenTranslation.name})
              </div>
              <a
                href="#"
                style={{
                  color: colors['darkPrimary'],
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  navigateBible(currentLocation, { query: '' })
                  setQueryInput('')
                }}
              >
                Clear query
              </a>
            </div>
          ) : (
            <>
              <BooksList
                currentTranslation={chosenTranslation}
                currentBook={chosenBook}
                currentChapter={chosenChapter}
                books={queriedBooks}
                chosenBook={chosenBook}
                scrollToChosenOnUpdate={!queryInput}
              />
              {queriedVerses && (
                <div style={{
                  marginTop: queriedBooks?.length ? '4px' : 0,
                }}>
                  <QueriedVerses
                    currentTranslation={chosenTranslation}
                    currentBook={chosenBook}
                    currentChapter={chosenChapter}
                    verses={queriedVerses}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
